import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Lightbulb } from "lucide-react";
import { HERO_SLIDES } from "../data/products";

const TRUST = ["PajamaJeans®", "Total Pillow®", "Twin Draft Guard®", "Wonder Hanger®"];

export const Hero = () => {
  const [index, setIndex] = useState(0);
  const count = HERO_SLIDES.length;

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % count), 4500);
    return () => clearInterval(t);
  }, [count, index]);

  const slide = HERO_SLIDES[index];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-brand-bg pt-24"
    >
      <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#8DC63F]/10 blur-[130px]" />
      <div className="pointer-events-none absolute bottom-0 -left-40 h-[460px] w-[460px] rounded-full bg-[#13355E]/60 blur-[130px]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-2 gap-16 items-center py-16">
        {/* brand messaging */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-[#8DC63F]/30 bg-[#8DC63F]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-[#8DC63F]">
            Leaders in “As Seen on TV” Innovation
          </span>

          <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-brand-text leading-[0.95]">
            Enriching life
            <br />
            <span className="text-[#8DC63F]">through innovation</span>
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-brand-muted">
            Hampton Direct develops, brands and markets some of the most successful
            consumer products in direct-response history — turning everyday household
            problems into simple, satisfying solutions for families around the world.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#products"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#8DC63F] px-7 py-3.5 text-sm font-semibold text-[#050A15] hover:bg-[#7AB034] transition-colors"
            >
              Explore Our Products
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#inventor"
              data-testid="hero-inventor-button"
              className="inline-flex items-center gap-2 rounded-full border border-brand-border px-7 py-3.5 text-sm font-semibold text-brand-text hover:border-[#8DC63F]/50 transition-colors"
            >
              <Lightbulb size={16} className="text-[#8DC63F]" /> Got an Invention?
            </a>
          </div>

          <div className="mt-12">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
              Trusted household brands
            </p>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
              {TRUST.map((b) => (
                <span key={b} className="text-sm font-medium text-slate-300">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* product showcase visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative"
        >
          <div className="relative mx-auto max-w-md rounded-3xl border border-brand-border bg-brand-surface p-5">
            <div className="relative h-[360px] sm:h-[420px] overflow-hidden rounded-2xl bg-gradient-to-br from-white to-slate-200">
              <AnimatePresence mode="wait">
                <motion.img
                  key={slide.id}
                  src={slide.image}
                  alt={slide.title}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 h-full w-full object-contain p-6"
                />
              </AnimatePresence>
            </div>

            <div className="mt-4 flex items-center justify-between px-1">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8DC63F]">
                  Featured Product
                </p>
                <p className="font-heading text-lg font-semibold text-brand-text">
                  {slide.title}
                </p>
              </div>
              <div className="flex gap-1.5">
                {HERO_SLIDES.map((s, i) => (
                  <button
                    key={s.id}
                    data-testid={`hero-dot-${i}`}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === index ? "w-7 bg-[#8DC63F]" : "w-2.5 bg-brand-muted2/40"
                    }`}
                    aria-label={`Show ${s.title}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
